import './featuredMissions.scss';
import 'slick-carousel/slick/slick.css';
import { type Maybe, useScreenSize } from '@custom/dx3';
import { Button } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type SliderType from 'react-slick';
import Slider_ from 'react-slick';
import { COPY } from '../../assets/copy/copy';
import { LeftArrow, LockIcon, RightArrow, StarIcon } from '../../assets/svgs';
import ROUTES from '../../routes/routes.js';
import { usNumberFormatter } from '../../utils/formatters';
import { MISSION_ID_PARAM } from '../../utils/routingHelpers.js';
import { ActionTag } from '../ActionTag/ActionTag';
import { StatusIcons } from '../StatusIcons/StatusIcons';

// Something is screwing with our ability to import default-exported things
// -- we ask for the default export, and esbuild gives us the entire module!
// I've tried looking into this, but I give up for now.
const Slider = (Slider_ as any).default as typeof Slider_;

export type FeaturedMission = {
  expiringDate?: Maybe<Date | string | number>;
  id: string | null;
  imageUrl: Maybe<string>;
  textColor?: Maybe<string>;
  name: Maybe<string>;
  description: Maybe<string>;
  point: Maybe<{ count: Maybe<number>; category: Maybe<string> }>;
  locked: boolean;
  inProgress?: Maybe<boolean>;
  repeats?: Maybe<boolean>;
  expiringSoon?: Maybe<boolean>;
  alternativeButtonText?: string;
};

type FeaturedMissionsProps = {
  missions: Maybe<FeaturedMission[]>;
};

export const FeaturedMissions = (props: FeaturedMissionsProps) => {
  const { missions } = props;
  const isMobile = useScreenSize() === 'mobile';

  const carouselRef = useRef<SliderType>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSlideChange = useCallback(
    (index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex]
  );

  const slideForward = useCallback(() => {
    if (missions) {
      const nextIndex = (selectedIndex + 1) % missions.length;
      if (carouselRef.current) {
        carouselRef.current.slickGoTo(nextIndex);
      }
    }
  }, [missions, selectedIndex]);

  const slideBack = useCallback(() => {
    if (missions) {
      let nextIndex = selectedIndex - 1;
      if (nextIndex < 0) {
        nextIndex = missions?.length - 1;
      }
      if (carouselRef.current) {
        carouselRef.current.slickGoTo(nextIndex);
      }
    }
  }, [missions, selectedIndex]);

  const controls = useMemo(() => {
    if (!missions || missions.length <= 1) return null;

    return (
      <div className="controls">
        <ActionTag>
          {selectedIndex + 1} / {missions.length}
        </ActionTag>

        <ActionTag
          className={'control-action'}
          onClick={slideBack}
          arialLabel={COPY.FEATURED_MISSION.ARIA.BACKWARD}
        >
          <LeftArrow style={{ verticalAlign: 'middle' }} />
        </ActionTag>

        <ActionTag
          className={'control-action'}
          onClick={slideForward}
          arialLabel={COPY.FEATURED_MISSION.ARIA.FORWARD}
        >
          <RightArrow style={{ verticalAlign: 'middle' }} />
        </ActionTag>
      </div>
    );
  }, [missions, selectedIndex, slideBack, slideForward]);

  const slides = useMemo(() => {
    if (!missions || missions.length === 0) return null;

    return missions.map((mission) => {
      const textColor =
        isMobile ||
        !mission.textColor ||
        !CSS.supports('color', mission.textColor)
          ? '#000'
          : mission.textColor;

      const textColorStyle = { color: textColor };

      return (
        <div
          key={mission.id}
          className={`mission-slide-container ${mission.locked ? 'grey' : ''}`}
        >
          <div
            className="mission-slide"
            style={{
              backgroundImage: `url("${mission.imageUrl}")`,
            }}
          >
            <div className="status-icon">
              {mission.locked ? (
                <ActionTag icon={<LockIcon />}>
                  {COPY.MISSIONS.LOCKED}
                </ActionTag>
              ) : (
                <ActionTag icon={<StarIcon />}>
                  {COPY.MISSIONS.FEATURED}
                </ActionTag>
              )}
            </div>
          </div>
          <div className="card-content-wrapper">
            <div className="card-content">
              {/* Using dumby div to end at the same point as the featured/locked tag to keep spacing even. */}
              <div className="dumby-div" />
              <h3 style={textColorStyle} className="mission-name bold">
                {mission.name}
              </h3>
              <p style={textColorStyle} className="mission-description">
                {mission.description}
              </p>
              <h4 style={textColorStyle} className="bold">
                {mission.point?.count &&
                  usNumberFormatter.format(mission.point.count)}
                {mission.point?.category && mission.point.category}
              </h4>
              <div style={textColorStyle}>
                <StatusIcons
                  expiringDate={mission.expiringDate}
                  expiringSoon={mission.expiringSoon}
                  repeats={mission.repeats}
                />
              </div>
              <div>
                {mission.id && (
                  <Button
                    className="hero-button small-text bold"
                    component={Link}
                    to={`${ROUTES.HOME}?${new URLSearchParams({
                      [MISSION_ID_PARAM]: mission.id,
                    })}${location.hash}`}
                    variant="contained"
                  >
                    {mission.alternativeButtonText ??
                      COPY.FEATURED_MISSION.BUTTON_TEXT}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [isMobile, missions]);

  return (
    <div className="featured-missions-container">
      {controls}
      <div className="carousel-container">
        <Slider
          ref={carouselRef}
          arrows={false}
          dots={false}
          infinite={true}
          afterChange={onSlideChange}
        >
          {slides}
        </Slider>
      </div>
    </div>
  );
};
